/**
 * premium-hero-slider.js
 *
 * Handles all slider interactivity: autoplay, arrow navigation, dot navigation,
 * touch/swipe support, keyboard navigation, hover-pause, and visibility-pause.
 *
 * Configuration is injected per-section by the .liquid file into:
 *   window.__premiumSliderConfig[sectionId]
 *
 * This file is loaded once and automatically initialises every slider instance
 * on the page, including those added dynamically (Shopify Theme Editor).
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     Core slider factory
  ───────────────────────────────────────────── */
  function initSlider(sectionId) {
    // Resolve the wrapping Shopify section element so multiple
    // instances on the same page are fully independent.
    const sectionEl = document.getElementById('shopify-section-' + sectionId);
    if (!sectionEl) return;

    const sliderContainer = sectionEl.querySelector('.premium-slider-container');
    if (!sliderContainer) return;

    const slides  = sliderContainer.querySelectorAll('.premium-slide');
    const dots    = sliderContainer.querySelectorAll('.premium-slider-dot');
    const prevBtn = sliderContainer.querySelector('.premium-slider-arrow.prev');
    const nextBtn = sliderContainer.querySelector('.premium-slider-arrow.next');

    if (!slides.length) return;

    // Pull config injected by Liquid; fall back to sensible defaults.
    const rawConfig = (window.__premiumSliderConfig && window.__premiumSliderConfig[sectionId]) || {};
    const config = {
      autoplay:           rawConfig.autoplay           !== undefined ? rawConfig.autoplay : true,
      speed:              rawConfig.speed              || 5000,
      transitionDuration: rawConfig.transitionDuration || 600,
    };

    let currentIndex     = 0;
    let autoplayInterval = null;
    let isTransitioning  = false;

    /* ── goToSlide ── */
    function goToSlide(index) {
      if (isTransitioning || index === currentIndex) return;

      isTransitioning = true;

      slides.forEach(function (slide) { slide.classList.remove('active'); });
      dots.forEach(function (dot)     { dot.classList.remove('active');   });

      currentIndex = index;
      slides[currentIndex].classList.add('active');
      if (dots[currentIndex]) {
        dots[currentIndex].classList.add('active');
      }

      setTimeout(function () {
        isTransitioning = false;
      }, config.transitionDuration);
    }

    /* ── nextSlide / prevSlide ── */
    function nextSlide() {
      goToSlide((currentIndex + 1) % slides.length);
    }

    function prevSlide() {
      goToSlide((currentIndex - 1 + slides.length) % slides.length);
    }

    /* ── Autoplay ── */
    function startAutoplay() {
      if (config.autoplay && slides.length > 1) {
        stopAutoplay();
        autoplayInterval = setInterval(nextSlide, config.speed);
      }
    }

    function stopAutoplay() {
      if (autoplayInterval) {
        clearInterval(autoplayInterval);
        autoplayInterval = null;
      }
    }

    /* ── Arrow buttons ── */
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        prevSlide();
        stopAutoplay();
        startAutoplay();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        nextSlide();
        stopAutoplay();
        startAutoplay();
      });
    }

    /* ── Dot navigation ── */
    dots.forEach(function (dot, index) {
      dot.addEventListener('click', function () {
        goToSlide(index);
        stopAutoplay();
        startAutoplay();
      });
    });

    /* ── Touch / Swipe support ── */
    var touchStartX = 0;
    var touchEndX   = 0;

    sliderContainer.addEventListener('touchstart', function (e) {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    sliderContainer.addEventListener('touchend', function (e) {
      touchEndX = e.changedTouches[0].screenX;
      handleSwipe();
    }, { passive: true });

    function handleSwipe() {
      var swipeThreshold = 50;
      var diff = touchStartX - touchEndX;
      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          nextSlide();
        } else {
          prevSlide();
        }
        stopAutoplay();
        startAutoplay();
      }
    }

    /* ── Keyboard navigation ── */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        prevSlide();
        stopAutoplay();
        startAutoplay();
      } else if (e.key === 'ArrowRight') {
        nextSlide();
        stopAutoplay();
        startAutoplay();
      }
    });

    /* ── Hover-pause (desktop / pointer devices only) ── */
    if (window.matchMedia('(hover: hover)').matches) {
      sliderContainer.addEventListener('mouseenter', stopAutoplay);
      sliderContainer.addEventListener('mouseleave', startAutoplay);
    }

    /* ── Visibility-change pause ── */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stopAutoplay();
      } else {
        startAutoplay();
      }
    });

    /* ── Kick off ── */
    if (slides.length > 1) {
      startAutoplay();
    }
  }

  /* ─────────────────────────────────────────────
     Initialise all sliders already in the DOM
  ───────────────────────────────────────────── */
  function initAll() {
    var configs = window.__premiumSliderConfig || {};
    Object.keys(configs).forEach(function (sectionId) {
      initSlider(sectionId);
    });
  }

  /* ─────────────────────────────────────────────
     Shopify Theme Editor support
     Re-initialise whenever the merchant adds/
     reorders/removes a section or block.
  ───────────────────────────────────────────── */
  document.addEventListener('shopify:section:load', function (e) {
    var sectionId = e.detail && e.detail.sectionId;
    if (sectionId) {
      initSlider(sectionId);
    }
  });

  document.addEventListener('shopify:block:select', function (e) {
    var sectionId = e.detail && e.detail.sectionId;
    if (sectionId) {
      initSlider(sectionId);
    }
  });

  /* ─────────────────────────────────────────────
     Boot
  ───────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

})();